import {Text} from 'quickstart/components/content/Text'
import {Checkbox} from 'quickstart/components/controls/Checkbox'
import {Radios} from 'quickstart/components/controls/Radio'
import {Spinner} from 'quickstart/components/layout'
import {Accordion} from 'quickstart/components/layout/Accordion'
import {Box} from 'quickstart/components/layout/Box'
import {SearchForm} from 'quickstart/components/tizra/SearchForm'
import {
  type UseSearchReturn,
  useEqualBy,
  useHitCounts,
  useSticky,
} from 'quickstart/hooks'
import {
  type SearchField,
  formFieldKey,
  formFields,
} from 'quickstart/lib/search/config'
import {cleanOptions} from 'quickstart/lib/search/options'
import * as R from 'rambdax'
import * as React from 'react'
import * as Final from 'react-final-form'
import {ensureArray, logger, semiSplit} from 'tizra'

const log = logger('SearchFacets')

const MAX_RENDER_CHECKBOXES_DEFAULT = 400

const NoSelectionsMessage = (props: React.ComponentProps<typeof Box>) => (
  <Box color="textColorMuted" {...props}>
    No selections available for your search criteria
  </Box>
)

interface SearchFacetsConfigFieldProps {
  field: SearchField
  search: UseSearchReturn
  visible: boolean
  setLoadingHits: (name: string, loading: boolean) => void
}

const SearchFacetsConfigField = ({
  field,
  field: {name},
  search,
  visible,
  setLoadingHits,
}: SearchFacetsConfigFieldProps) => {
  let {data: hitCounts, loading: loadingHits} = useHitCounts({
    field,
    search,
    visible,
  })
  hitCounts = useSticky(hitCounts)

  React.useEffect(
    () => setLoadingHits(name, loadingHits),
    [name, setLoadingHits, loadingHits],
  )

  const valueSpec = React.useMemo<Record<string, string>>(
    () =>
      field.serverDef?.valueSpecification && !field.serverDef.isCalculated
        ? R.piped(
            field.serverDef.valueSpecification,
            semiSplit,
            R.map(s => s.split('|', 2).map(ss => ss.trim())),
            R.map(vk => [vk[1] || vk[0], vk[0] || vk[1]] as [string, string]),
            kvs => kvs.filter(([k]) => k),
            kvs => R.fromPairs(kvs),
          )
        : {},
    [field.serverDef],
  )

  const options = React.useMemo(
    () =>
      cleanOptions(field.options).map(o => ({
        ...o,
        label: valueSpec[o.value] || o.label,
      })),
    [field.options, valueSpec],
  )

  const valueSet = useEqualBy(
    R.equals,
    new Set(ensureArray(search.params[field.name])),
  )

  const facet: SearchField = React.useMemo(() => {
    const widget =
      field.widget ||
      (Array.isArray(field.defaultValue) ? 'checklist' : 'radio')
    const maxRender =
      field.maxRender ||
      (['checklist', 'radio'].includes(widget) &&
        MAX_RENDER_CHECKBOXES_DEFAULT) ||
      Infinity
    const filteredOptions = !hitCounts
      ? options
      : options.filter(({value: v}) => hitCounts[v] || valueSet?.has(v))
    const relabeledOptions =
      !hitCounts || loadingHits
        ? filteredOptions
        : filteredOptions.map(o => ({
            ...o,
            label: `${o.label} (${hitCounts[o.value]})`,
          }))
    const limitedOptions =
      relabeledOptions.length > maxRender
        ? relabeledOptions.slice(0, maxRender)
        : relabeledOptions
    return {
      ...field,
      widget,
      options: limitedOptions,
    }
  }, [field, hitCounts, loadingHits, options, valueSet])

  let rendered = (
    <div>
      {facet.name} {facet.widget}
    </div>
  )

  if (facet.widget === 'radio') {
    rendered = !facet.options?.length ? (
      <NoSelectionsMessage
        style={{visibility: facet.loading ? 'hidden' : 'visible'}}
      />
    ) : (
      <Final.Field
        name={facet.name}
        render={({input}) => (
          <Text variant="form">
            <Radios options={options} {...input} />
          </Text>
        )}
      />
    )
  }

  if (facet.widget === 'checklist') {
    rendered = !facet.options?.length ? (
      <NoSelectionsMessage
        style={{visibility: facet.loading ? 'hidden' : 'visible'}}
      />
    ) : (
      <Final.Field
        name={facet.name}
        render={({input}) =>
          !!visible && (
            <Text variant="form">
              <Checkbox.Group options={facet.options as any} {...input} />
            </Text>
          )
        }
      />
    )
  }

  return (
    <>
      {rendered}
      <Spinner loading={facet.loading} />
    </>
  )
}

type SearchFacetsProps = Omit<
  React.ComponentProps<typeof Accordion.Group>,
  'children'
> & {
  search: UseSearchReturn
}

export const SearchFacets = ({
  search,
  search: {config},
  ...rest
}: SearchFacetsProps) => {
  // Memoize so that fs/ffks don't change on every render, causing looping
  // renders via effects.
  const [fs, ffks, defaultOpen] = React.useMemo(() => {
    const fs = formFields(config, 'desktopFilters').filter(
      // We need to filter out the terms field explicitly, since it appears
      // at the top of the results instead of next to them.
      f => f.name !== 'terms',
    )
    // Yikes! Top-level defaultOpen can be boolean or array. Field-level can be
    // boolean or undefined. Convert to the simplest representation to pass to
    // Accordion.Group.
    const defaultOpen =
      fs.every(f => f.defaultOpen === undefined) &&
      typeof config.defaultOpen === 'boolean'
        ? config.defaultOpen
        : fs.flatMap((f, i) =>
            f.defaultOpen === true
              ? [i]
              : f.defaultOpen === false
                ? []
                : Array.isArray(config.defaultOpen)
                  ? config.defaultOpen.includes(i) ||
                    config.defaultOpen.includes(f.name)
                    ? [i]
                    : []
                  : config.defaultOpen
                    ? [i]
                    : [],
          )
    return [fs, fs.map(formFieldKey), defaultOpen]
  }, [config])

  const [loadingHits, _setLoadingHits] = React.useState<Set<string>>(new Set())
  const setLoadingHits = React.useCallback(
    (name: string, loading: boolean) =>
      _setLoadingHits(prevSet => {
        const prevLoading = prevSet.has(name)
        if (loading === prevLoading) return prevSet
        const nextSet = new Set(prevSet)
        nextSet[loading ? 'add' : 'delete'](name)
        return nextSet
      }),
    [],
  )

  return (
    <SearchForm instant search={search}>
      <Accordion.Group defaultOpen={defaultOpen} {...rest}>
        {fs.map(({label, ...field}, i) => (
          <Accordion
            key={ffks[i]}
            title={
              <span>
                {label}&nbsp;
                <Spinner inline loading={loadingHits.has(field.name)} />
              </span>
            }
            style={{position: 'relative'}}
          >
            {({visible}) => {
              return (
                <SearchFacetsConfigField
                  field={field}
                  search={search}
                  visible={visible}
                  setLoadingHits={setLoadingHits}
                />
              )
            }}
          </Accordion>
        ))}
      </Accordion.Group>
    </SearchForm>
  )
}
